import { needsBody, partsHaveVars, tryParseFormData } from './requestParts';

// ─── PHP ───────────────────────────────────────────────────────────────────

export const DEFAULT_PHP_OPTIONS = {
  timeout:         0,
  verifySsl:       true,
  followRedirects: false,
};

export function generatePHP(parts, options = {}) {
  const {
    timeout         = DEFAULT_PHP_OPTIONS.timeout,
    verifySsl       = DEFAULT_PHP_OPTIONS.verifySsl,
    followRedirects = DEFAULT_PHP_OPTIONS.followRedirects,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);

  lines.push('<?php\n');
  lines.push('$curl = curl_init();\n');
  lines.push('curl_setopt_array($curl, [');
  lines.push(`    CURLOPT_URL => "${parts.url.replace(/"/g, '\\"')}",`);
  lines.push('    CURLOPT_RETURNTRANSFER => true,');
  lines.push(`    CURLOPT_CUSTOMREQUEST => "${parts.method}",`);
  if (timeout > 0)      lines.push(`    CURLOPT_TIMEOUT => ${timeout},`);
  if (!verifySsl)       lines.push('    CURLOPT_SSL_VERIFYPEER => false,');
  if (followRedirects)  lines.push('    CURLOPT_FOLLOWLOCATION => true,');

  if (withBody) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        lines.push('    CURLOPT_POSTFIELDS => [');
        for (const f of fields) {
          lines.push(`        "${f.key}" => "${(f.value || '').replace(/"/g, '\\"')}",`);
        }
        lines.push('    ],');
      } else {
        lines.push(`    CURLOPT_POSTFIELDS => '${parts.body.replace(/'/g, "\\'")}',`);
      }
    } else {
      lines.push(`    CURLOPT_POSTFIELDS => '${parts.body.replace(/'/g, "\\'")}',`);
    }
  }

  if (headerEntries.length) {
    lines.push('    CURLOPT_HTTPHEADER => [');
    for (const [k, v] of headerEntries) {
      lines.push(`        "${k}: ${v.replace(/"/g, '\\"')}",`);
    }
    lines.push('    ],');
  }

  lines.push(']);\n');
  lines.push('$response = curl_exec($curl);');
  lines.push('$httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);');
  lines.push('curl_close($curl);\n');
  lines.push('echo $httpCode . PHP_EOL;');
  lines.push('echo $response . PHP_EOL;');

  return lines.join('\n');
}

// ─── Ruby ──────────────────────────────────────────────────────────────────

export const RUBY_METHOD_CLASS = {
  GET: 'Get', POST: 'Post', PUT: 'Put', DELETE: 'Delete',
  PATCH: 'Patch', HEAD: 'Head', OPTIONS: 'Options',
};

export const DEFAULT_RUBY_OPTIONS = {
  timeout:   0,
  verifySsl: true,
};

export function generateRuby(parts, options = {}) {
  const {
    timeout   = DEFAULT_RUBY_OPTIONS.timeout,
    verifySsl = DEFAULT_RUBY_OPTIONS.verifySsl,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('# Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);

  lines.push('require "net/http"');
  lines.push('require "uri"');
  if (!verifySsl) lines.push('require "openssl"');
  lines.push('');
  lines.push(`uri = URI.parse("${parts.url.replace(/"/g, '\\"')}")`);
  lines.push('http = Net::HTTP.new(uri.host, uri.port)');
  lines.push('http.use_ssl = uri.scheme == "https"');
  if (!verifySsl) lines.push('http.verify_mode = OpenSSL::SSL::VERIFY_NONE');
  if (timeout > 0) lines.push(`http.read_timeout = ${timeout}`);
  lines.push('');

  const klass = RUBY_METHOD_CLASS[parts.method] || parts.method.charAt(0).toUpperCase() + parts.method.slice(1).toLowerCase();
  lines.push(`request = Net::HTTP::${klass}.new(uri.request_uri)`);

  for (const [k, v] of headerEntries) {
    lines.push(`request["${k}"] = "${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
  }

  if (withBody) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        lines.push('request.set_form_data(');
        lines.push('  {');
        for (const f of fields) {
          lines.push(`    "${f.key}" => "${(f.value || '').replace(/"/g, '\\"')}",`);
        }
        lines.push('  }');
        lines.push(')');
      } else {
        lines.push(`request.body = "${parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
      }
    } else {
      lines.push(`request.body = "${parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    }
  }

  lines.push('');
  lines.push('response = http.request(request)\n');
  lines.push('puts response.code');
  lines.push('puts response.body');

  return lines.join('\n');
}

// ─── Swift ─────────────────────────────────────────────────────────────────

export const DEFAULT_SWIFT_OPTIONS = {
  timeout:        60,
  responseFormat: 'text',
};

export function generateSwift(parts, options = {}) {
  const {
    timeout        = DEFAULT_SWIFT_OPTIONS.timeout,
    responseFormat = DEFAULT_SWIFT_OPTIONS.responseFormat,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);

  lines.push('import Foundation\n');
  lines.push(`let url = URL(string: "${parts.url.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")!`);
  const timeoutArg = timeout !== 60
    ? `, cachePolicy: .useProtocolCachePolicy, timeoutInterval: ${timeout}`
    : '';
  lines.push(`var request = URLRequest(url: url${timeoutArg})`);
  lines.push(`request.httpMethod = "${parts.method}"`);

  for (const [k, v] of headerEntries) {
    lines.push(`request.setValue("${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}", forHTTPHeaderField: "${k.replace(/"/g, '\\"')}")`);
  }

  if (withBody) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        lines.push('');
        lines.push('var components = URLComponents()');
        lines.push('components.queryItems = [');
        for (const f of fields) {
          lines.push(`    URLQueryItem(name: "${f.key.replace(/"/g, '\\"')}", value: "${(f.value || '').replace(/"/g, '\\"')}"),`);
        }
        lines.push(']');
        lines.push('request.httpBody = components.query?.data(using: .utf8)');
      } else {
        const escaped = parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '');
        lines.push(`\nlet payload = "${escaped}"`);
        lines.push('request.httpBody = payload.data(using: .utf8)');
      }
    } else {
      // Use extended string literal to avoid escaping
      const needsExtended = parts.body.includes('"');
      if (needsExtended && !parts.body.includes('"#')) {
        lines.push(`\nlet payload = #"${parts.body.replace(/\n/g, '\n')}"#`);
      } else {
        const escaped = parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '');
        lines.push(`\nlet payload = "${escaped}"`);
      }
      lines.push('request.httpBody = payload.data(using: .utf8)');
    }
  }

  lines.push('');
  lines.push('let task = URLSession.shared.dataTask(with: request) { data, response, error in');
  lines.push('    if let error = error {');
  lines.push('        print("Error:", error.localizedDescription)');
  lines.push('        return');
  lines.push('    }');
  lines.push('    if let httpResponse = response as? HTTPURLResponse {');
  lines.push('        print("Status:", httpResponse.statusCode)');
  lines.push('    }');
  if (responseFormat === 'json') {
    lines.push('    if let data = data,');
    lines.push('       let json = try? JSONSerialization.jsonObject(with: data) {');
    lines.push('        print(json)');
    lines.push('    }');
  } else {
    lines.push('    if let data = data, let body = String(data: data, encoding: .utf8) {');
    lines.push('        print(body)');
    lines.push('    }');
  }
  lines.push('}');
  lines.push('task.resume()');

  return lines.join('\n');
}
